import { useState, useEffect } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Badge,
  Timeline,
  Loader,
  Button,
  ActionIcon,
  Avatar,
  Paper,
  ScrollArea
} from '@mantine/core';
import {
  IconBook,
  IconCards,
  IconCheck,
  IconX,
  IconTrophy,
  IconClock,
  IconStar,
  IconAlertCircle,
  IconRefresh,
  IconChevronRight,
  IconTarget
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { parentApi } from '@/features/parent/api/parentApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime);

interface Activity {
  id: string;
  type: 'story_completed' | 'quiz_taken' | 'flashcards_studied' | 'assignment_completed' |
        'streak_milestone' | 'achievement_earned' | 'goal_reached' | 'missed_assignment';
  childId: string;
  childName?: string;
  timestamp: string;
  data: {
    title?: string;
    score?: number;
    cardsStudied?: number;
    accuracy?: number;
    streakDays?: number;
    achievementName?: string;
    goalType?: string;
    readingTime?: number;
    difficulty?: string;
  };
}

interface MobileParentActivityFeedProps {
  childId?: string | null;
  limit?: number;
  showChildName?: boolean;
  autoRefresh?: boolean;
}

export function MobileParentActivityFeed({
  childId,
  limit = 10,
  showChildName = false,
  autoRefresh = false
}: MobileParentActivityFeedProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch activities
  const { data: activities, isLoading, refetch } = useQuery({
    queryKey: ['child-activities', childId, limit],
    queryFn: () => parentApi.getChildActivities(childId, limit),
    enabled: !!childId,
    refetchInterval: autoRefresh ? 30000 : false // Auto-refresh every 30 seconds if enabled
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'story_completed':
        return <IconBook size={16} />;
      case 'quiz_taken':
        return <IconCheck size={16} />;
      case 'flashcards_studied':
        return <IconCards size={16} />;
      case 'assignment_completed':
        return <IconTarget size={16} />;
      case 'streak_milestone':
        return <IconStar size={16} />;
      case 'achievement_earned':
        return <IconTrophy size={16} />;
      case 'goal_reached':
        return <IconTarget size={16} />;
      case 'missed_assignment':
        return <IconAlertCircle size={16} />;
      default:
        return <IconClock size={16} />;
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'story_completed':
        return 'blue';
      case 'quiz_taken':
        return 'green';
      case 'flashcards_studied':
        return 'violet';
      case 'assignment_completed':
        return 'teal';
      case 'streak_milestone':
        return 'orange';
      case 'achievement_earned':
        return 'yellow';
      case 'goal_reached':
        return 'lime';
      case 'missed_assignment':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getActivityTitle = (activity: Activity) => {
    switch (activity.type) {
      case 'story_completed':
        return `Read "${activity.data.title}"`;
      case 'quiz_taken':
        return `Quiz: ${activity.data.score}% on "${activity.data.title}"`;
      case 'flashcards_studied':
        return `Studied ${activity.data.cardsStudied} flashcards`;
      case 'assignment_completed':
        return `Completed "${activity.data.title}"`;
      case 'streak_milestone':
        return `${activity.data.streakDays} day reading streak! 🔥`;
      case 'achievement_earned':
        return `Earned "${activity.data.achievementName}" achievement`;
      case 'goal_reached':
        return `Reached ${activity.data.goalType} goal`;
      case 'missed_assignment':
        return `Missed: "${activity.data.title}"`;
      default:
        return 'Activity';
    }
  };

  const getActivityDescription = (activity: Activity) => {
    const timeAgo = dayjs(activity.timestamp).fromNow();
    let description = timeAgo;

    if (showChildName && activity.childName) {
      description = `${activity.childName} • ${description}`;
    }

    if (activity.data?.readingTime) {
      description += ` • ${activity.data.readingTime} min`;
    }

    if (activity.data?.accuracy !== undefined) {
      description += ` • ${activity.data.accuracy}% accuracy`;
    }

    return description;
  };

  if (isLoading) {
    return (
      <Card p="md" withBorder>
        <Group justify="center">
          <Loader size="sm" />
        </Group>
      </Card>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Card p="md" withBorder>
        <Stack align="center" gap="xs">
          <IconClock size={32} color="var(--mantine-color-dimmed)" />
          <Text size="sm" c="dimmed">
            No recent activity
          </Text>
          {childId && (
            <Button
              size="xs"
              variant="subtle"
              onClick={handleRefresh}
              loading={isRefreshing}
            >
              Check for updates
            </Button>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="xs">
      {/* Refresh button */}
      {!autoRefresh && (
        <Group justify="flex-end">
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={handleRefresh}
            loading={isRefreshing}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>
      )}

      {/* Activity Timeline */}
      <Timeline bulletSize={24} lineWidth={2}>
        {activities.map((activity) => (
          <Timeline.Item
            key={activity.id}
            bullet={getActivityIcon(activity.type)}
            color={getActivityColor(activity.type)}
          >
            <Paper p="xs" withBorder>
              <Stack gap={4}>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    {getActivityTitle(activity)}
                  </Text>
                  {activity.type === 'quiz_taken' && activity.data?.score && (
                    <Badge
                      size="sm"
                      variant="light"
                      color={activity.data.score >= 80 ? 'green' : 'yellow'}
                    >
                      {activity.data.score}%
                    </Badge>
                  )}
                  {activity.type === 'streak_milestone' && (
                    <Badge size="sm" variant="light" color="orange">
                      🔥 {activity.data?.streakDays}
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {getActivityDescription(activity)}
                </Text>
                {activity.data?.difficulty && (
                  <Badge size="xs" variant="dot">
                    {activity.data.difficulty}
                  </Badge>
                )}
              </Stack>
            </Paper>
          </Timeline.Item>
        ))}
      </Timeline>

      {/* Load more button if there might be more */}
      {activities.length === limit && (
        <Button
          variant="subtle"
          size="sm"
          rightSection={<IconChevronRight size={14} />}
          onClick={() => {
            // This would typically navigate to a full activity page
            // or load more items
          }}
        >
          View all activity
        </Button>
      )}
    </Stack>
  );
}